import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import LogoDark from '@images/logoDark.svg';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import FormFieldComponent from '@/components/FormFieldComponent';
import { useNavigate } from 'react-router-dom';
import { useUserStore } from '@/stores/user';
import { useEffect } from 'react';

const passwordSchema = z
  .string()
  .min(8, { message: 'Password must be at least 8 characters long' })
  .regex(/[A-Z]/, { message: 'Password must contain at least one uppercase letter' })
  .regex(/[a-z]/, { message: 'Password must contain at least one lowercase letter' })
  .regex(/[0-9]/, { message: 'Password must contain at least one number' })
  .regex(/[^A-Za-z0-9]/, { message: 'Password must contain at least one special character' });

const setFormSchema = z
  .object({
    newPassword: passwordSchema,
    retypeNewPassword: passwordSchema,
  })
  .refine((data) => data.newPassword === data.retypeNewPassword, {
    message: 'Passwords do not match. Please try again.',
    path: ['retypeNewPassword'],
  });

export const SetPassword = () => {
  const navigate = useNavigate();
  const userStore = useUserStore((state) => state);
  const form = useForm<z.infer<typeof setFormSchema>>({
    resolver: zodResolver(setFormSchema),
    defaultValues: {
      newPassword: '',
      retypeNewPassword: '',
    },
    mode: 'all',
  });
  const { formState } = form;
  useEffect(() => {
    if (userStore.user === null || userStore.resetPasswordToken === null) navigate('/login');
  }, [userStore]);

  const onSubmitHandler = () => {
    console.log('submit');
  };

  return (
    <div className="bg-white flex flex-col items-center justify-center h-screen min-h-[600px]">
      <form onSubmit={onSubmitHandler} className="w-[28%] min-w-[400px] max-w-[468px] max-h-[580px] ">
        <Card className="flex bg-white rounded-md w-full p-10 gap-6">
          <CardContent className="flex flex-col gap-10 w-full p-0">
            <div className="flex flex-col gap-6 justify-center items-center">
              <div>
                <img src={LogoDark} alt="logo" className="w-[135px] h-[70px]" />
              </div>
              <span className="text-center text-2xl font-semibold">Set Password</span>
            </div>
            <FormProvider {...form}>
              <FormFieldComponent
                control={form.control}
                name="newPassword"
                label="New password"
                placeholder="Enter New Password"
                required
                type="password"
                maxLength={64}
              />
              <FormFieldComponent
                control={form.control}
                name="retypeNewPassword"
                label="Retype new password"
                placeholder="Retype New Password"
                required
                type="password"
                maxLength={64}
              />

              <div className="flex flex-col justify-start">
                <Button
                  type="submit"
                  className="w-full text-white rounded-md focus:outline-nones text-sm font-medium"
                  disabled={!formState.isValid}
                >
                  Set Password
                </Button>
              </div>
            </FormProvider>
          </CardContent>
        </Card>
      </form>
    </div>
  );
};
